/**
 * The screen that will not let a half-finished account into the app.
 *
 * **Why an account can be half-finished at all.** Sign-in stopped being one
 * door. Somebody arriving by phone code has a number and no address; somebody
 * arriving through Apple with a relayed address that collided has neither,
 * because the API refuses to store an address it cannot prove is theirs. Both
 * are correct at the moment they happen, and both leave an account nobody can
 * reach — one that cannot be recovered, cannot be linked to a second sign-in
 * method, and cannot be told anything.
 *
 * **The API already knew, and nothing acted on it.** `GET /me/completion` has
 * answered "what does this account still owe" since it was built; until this
 * file existed, an account with no email walked straight into the app. That is
 * the shape §4.0 keeps describing — a decision computed correctly and never
 * enforced — so the enforcement is the point of this file, not the form.
 *
 * **It renders from the server's list.** `requirements` come down with the
 * state. Adding a requirement is one
 * entry in `backend/.../completion.ts` and nothing here; this app does not need
 * shipping again to ask for it.
 *
 * **Anonymous visitors never see it.** Most people who open a festival's lineup
 * have no account and never will. This runs only when there is a session.
 *
 * **Since 2026-10-20 nobody sees it.** The user: "user
 * registers through google / apple / fb, don't need to verify the email …
 * gains access straight to the Find a rave page. Don't let the user go first
 * into complete registration. Only show a sub-banner suggesting to complete
 * registration … none are a must" — then "remove this — all optional". The
 * server marks nothing blocking, so this gate lets everybody through and
 * stays only for a requirement made blocking again; everything — phone, date of birth, name,
 * place, socials — is asked on the optional `/complete` page
 * (`CompleteProfilePage.tsx`), which reuses the steps below, and the banner
 * on My raves and Find a rave (`CompleteProfileBanner.tsx`) reads this
 * file's answer through `useCompletion()`.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { api, ApiError, onAccountIncomplete, takeSessionCompletion } from '@/lib/api';
import { NETWORK_TIMEOUT_MS } from '@/offline/net';
import {
  parseCalendarDate, isOldEnough, MINIMUM_AGE, PLATFORM_TERMS_CONFIRMATIONS, PLATFORM_TERMS_VERSION,
  type CalendarDate,
} from '@myrave/shared';
import { countryOptions, FALLBACK_NETWORKS, socialLink, type SocialNetwork } from '@/lib/profileFields';
import { Button, LoadingScreen } from '@/ui';
import { Field, Alert } from '@/visitor/authControls';
import { PhoneField } from '@/visitor/PhoneField';
import { useAuth } from './AuthProvider';
import { useLocale } from './LocaleProvider';
import { rich } from './rich';
import { useT } from '@/i18n';
import './CompleteAccount.css';

export interface Requirement {
  key: string;
  label: string;
  why: string;
  blocking: boolean;
  /** The terms' version to accept (`terms` only). */
  version?: string;
  /** `terms` only: the documents already agreed — the step resumes after them. */
  confirmed?: string[];
}

export interface CompletionState {
  /** Every BLOCKING requirement satisfied. */
  complete: boolean;
  /** Nothing left at all. */
  finished: boolean;
  missing: string[];
  outstanding: Requirement[];
  requirements: Requirement[];
}

/**
 * Where the check has got to.
 *
 * **`pending` and `failed` are different and used to be the same thing.** Both
 * were `state === null`, and both rendered the app — so a signed-in person saw
 * the dashboard, and then the gate interrupted a second later when the answer
 * arrived. Reported 2026-09-26: "saw the app dashboard, after a few seconds
 * finish your account appeared."
 *
 * The distinction preserves the rule that mattered: a check that FAILS must
 * never be what stands between somebody and the schedule they saved — a
 * festival is a field with no reception. A check that has not answered YET is
 * not that, and showing the app during it is a promise the next render breaks.
 */
type Check =
  | { status: 'pending' }
  | { status: 'ready'; state: CompletionState }
  | { status: 'failed' };

/** How long the completion check may take before the app is let through. */
export const COMPLETION_TIMEOUT_MS = NETWORK_TIMEOUT_MS;

/**
 * Does this answer let the person into the app? Only a BLOCKING requirement
 * stops it — and since 2026-10-20 the server marks none.
 */
function passes(state: CompletionState): boolean {
  return state.finished || !state.outstanding.some((r) => r.blocking);
}

/**
 * What this gate knows, for the screens inside it (2026-10-20): the banner on
 * My raves and Find a rave shows while `finished` is false, and the optional
 * `/complete` page marks each section done and refreshes the answer after
 * every save, so the banner leaves the moment nothing is left.
 *
 * `status` is the gate's own: `none` signed out (or outside the gate),
 * `pending` while it asks, `failed` when it could not tell — offline, no
 * answer in time — and then nothing is suggested.
 */
export interface CompletionView {
  status: 'none' | Check['status'];
  state: CompletionState | null;
  /** Ask `/me/completion` again, in the background. Never throws. */
  refresh: () => Promise<void>;
  /** Take an answer a save already carried (`/me/contact/confirm`, the birthday). */
  update: (next: CompletionState) => void;
}

const CompletionContext = createContext<CompletionView>({
  status: 'none', state: null, refresh: async () => {}, update: () => {},
});

export function useCompletion(): CompletionView {
  return useContext(CompletionContext);
}

/** Calls `onNavigate` whenever the route changes — never on the first render. */
function NavigationWatcher({ onNavigate }: { onNavigate: () => void }) {
  const { pathname } = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    onNavigate();
  }, [pathname, onNavigate]);
  return null;
}

export function CompleteAccount({ children }: { children: ReactNode }) {
  const tr = useT();
  const { user, loading, offline } = useAuth();
  const [check, setCheck] = useState<Check>({ status: 'pending' });
  const setState = (next: CompletionState) => setCheck({ status: 'ready', state: next });
  const userId = user?.id ?? null;
  /**
   * The person whose verdict is on screen — the app, or the gate.
   *
   * **The check is keyed on WHO, never on the `user` object.** Every token
   * refresh (every ~14 minutes, on the page coming back into view, on each
   * 401 retried) used to hand this a new object for the same person, the
   * effect below dropped to `pending`, and "One moment…" unmounted the whole
   * app — an open sheet, a half-typed form, the map's zoom — to ask a
   * question already answered. Once a person has a verdict, asking again
   * happens in the background and never takes the screen away.
   */
  const verdictFor = useRef<string | null>(null);
  /**
   * An answer that arrived after the app was let through, and says "finish
   * first". It is held until the next navigation rather than applied at once:
   * tearing down a screen somebody is in the middle of using, seconds after
   * showing it, is the flash this component exists to prevent. The API still
   * refuses (`account_incomplete`) whatever the app shows meanwhile, and that
   * refusal — handled below — does open the gate straight away.
   */
  const late = useRef<CompletionState | null>(null);

  useEffect(() => {
    if (loading || !userId) { verdictFor.current = null; late.current = null; setCheck({ status: 'pending' }); return; }
    const shown = verdictFor.current === userId;
    const decide = (next: Check) => { verdictFor.current = userId; setCheck(next); };
    // An unverified (offline) session: the check would hang on the same wifi
    // the boot refresh just gave up on. Let the app through, as a failed
    // check does; it runs when the session comes back. If the app is already
    // showing for this person, it stays exactly as it is — flipping to
    // `failed` and back is the flash.
    if (offline) { if (!shown) decide({ status: 'failed' }); return; }
    // The refresh that brought this session already carried the answer.
    const known = shown ? null : takeSessionCompletion(userId) as CompletionState | null;
    if (known) { decide({ status: 'ready', state: known }); return; }
    let cancelled = false;
    let timedOut = false;
    if (!shown) setCheck({ status: 'pending' });
    /*
     * The same 2.5s ceiling as the boot refresh. On festival wifi a request
     * neither answers nor fails, and "One moment…" used to be the whole app
     * for as long as it hung. No answer in time is "cannot tell" — the app is
     * let through, and the server still refuses an unfinished account.
     */
    const timer = setTimeout(() => {
      if (cancelled || verdictFor.current === userId) return;
      timedOut = true;
      decide({ status: 'failed' });
    }, COMPLETION_TIMEOUT_MS);
    api
      .get<CompletionState>('/me/completion')
      .then((next) => {
        clearTimeout(timer);
        if (cancelled) return;
        if (!shown && !timedOut) { decide({ status: 'ready', state: next }); return; }
        // A background answer. One that lets the app through changes nothing
        // on screen (same children, same place in the tree: no remount).
        setCheck((now) => {
          if (passes(next) || (now.status === 'ready' && !passes(now.state))) {
            late.current = null;
            return { status: 'ready', state: next };
          }
          late.current = next;
          return now;
        });
      })
      // A completion check that FAILS must never be what stands between
      // somebody and the app. Offline is the normal case at a festival — and
      // it is the only case that lets the app through unchecked.
      .catch(() => {
        clearTimeout(timer);
        if (!cancelled && !shown && !timedOut) decide({ status: 'failed' });
      });
    return () => { cancelled = true; clearTimeout(timer); };
  }, [loading, userId, offline]);

  /** The next navigation applies an answer that was held back (see `late`). */
  const onNavigate = useCallback(() => {
    const next = late.current;
    if (!next) return;
    late.current = null;
    setCheck({ status: 'ready', state: next });
  }, []);
  const inRouter = useInRouterContext();

  /*
   * The API said "finish first" (403 `account_incomplete`) to some request
   * the app made. Ask again what is outstanding — in the BACKGROUND, keeping
   * whatever is on screen until the answer lands. Dropping to `pending` would
   * unmount the app, remount it, re-send the refused request and come straight
   * back here: a loop with a logo in it.
   */
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let inFlight = false;
    const off = onAccountIncomplete(() => {
      if (inFlight) return;
      inFlight = true;
      api
        .get<CompletionState>('/me/completion')
        .then((next) => { if (!cancelled) { late.current = null; setCheck({ status: 'ready', state: next }); } })
        .catch(() => {})
        .finally(() => { inFlight = false; });
    });
    return () => { cancelled = true; off(); };
  }, [userId]);

  /** The screens inside ask again after a save (the `/complete` page). */
  const refresh = useCallback(async () => {
    try {
      const next = await api.get<CompletionState>('/me/completion');
      late.current = null;
      setCheck({ status: 'ready', state: next });
    } catch { /* nothing to suggest is the honest answer offline */ }
  }, []);
  const update = useCallback((next: CompletionState) => {
    late.current = null;
    setCheck({ status: 'ready', state: next });
  }, []);
  const view = useMemo<CompletionView>(() => ({
    status: user ? check.status : 'none',
    state: user && check.status === 'ready' ? check.state : null,
    refresh,
    update,
  }), [user, check, refresh, update]);

  return <CompletionContext.Provider value={view}>{gate()}</CompletionContext.Provider>;

  function gate(): ReactNode {
  /*
   * Every branch that shows the app returns THIS, so moving between them (a
   * failed check, then a background answer that lets the person through) is
   * the same tree in the same place — React keeps it, nothing remounts.
   */
  const app = <>{children}{inRouter && <NavigationWatcher onNavigate={onNavigate} />}</>;

  // Anonymous visitors pass straight through, and the endpoint is never called.
  // Most people who open a lineup have no account and never will.
  if (!user) return app;

  /*
   * Waiting, not deciding. Rendering the app here is what produced the flash:
   * the dashboard appeared and was taken away a second later.
   */
  if (check.status === 'pending') return <LoadingScreen label={tr('One moment…')} />;

  // Offline, or the API is unreachable. See the catch above.
  if (check.status === 'failed') return app;

  const state = check.state;
  if (state.finished) return app;

  // Blocking, and no way past it.
  const blocking = state.outstanding.find((r) => r.blocking);
  if (blocking) {
    /*
     * **The MyRave Terms of Use come first** (the user, 2026-10-21: "when a
     * new user registers he also needs to approve the global terms of use").
     * The server lists `terms` first and marks it blocking for every account
     * that has not accepted the version in force.
     */
    if (blocking.key === 'terms') return <TermsStep version={blocking.version} confirmed={blocking.confirmed} onDone={setState} />;

    /*
     * **Date of birth is not a contact**, so it does not go through `Gate`.
     * There is no code to send and nothing to prove — and, unlike every other
     * requirement here, it is one some people cannot satisfy at all.
     *
     * Since 2026-10-20 the server never marks it (or the profile) blocking;
     * these two branches stay so a requirement made blocking again needs no
     * release here — the steps themselves now live on the optional page.
     */
    if (blocking.key === 'birthDate') {
      return <BirthDateStep requirement={blocking} onDone={setState} />;
    }

    /*
     * **The profile is ONE screen** (step 68, 2026-10-15): whatever of name,
     * place and social link is still missing, asked together — three screens
     * for three short answers would read as an interrogation.
     */
    if (PROFILE_KEYS.has(blocking.key)) {
      return (
        <ProfileStep
          missing={state.outstanding.filter((r) => r.blocking && PROFILE_KEYS.has(r.key))}
          onDone={setState}
        />
      );
    }

    return (
      <Gate
        requirement={blocking}
        remaining={state.outstanding.filter((r) => r.blocking).length}
        /**
         * Every account registered with a password before this existed holds an
         * address nobody ever checked — `emailVerified` was only ever set by an
         * OAuth provider. Those people are not adding an address, they are
         * proving the one they already gave us, and making them retype it reads
         * as the app having lost it.
         */
        prefill={blocking.key === 'email' ? user.email ?? '' : ''}
        onDone={setState}
      />
    );
  }

  /*
   * Nothing blocking: the app. A non-blocking requirement opens no screen
   * here — since 2026-10-20 every one is asked on the optional `/complete`
   * page, which the banner on My raves and Find a rave points to (the user:
   * "Don't let the user go first into complete registration").
   */
  return app;
  }
}

/**
 * The MyRave Terms of Use and Privacy Policy — ACCEPTED BY SIGNING IN (the user, 2026-10-26:
 * "on the sign-in screen, big at the bottom: when signing in you are accepting the terms of use
 * and privacy, with the links to read them; the user doesn't need to check anything … and remove
 * the two pages of approval"). The sign-in page says so (`LoginPage`); here the acceptance is
 * RECORDED — `POST /me/completion/terms { version, confirmations: [terms, privacy] }`, the same
 * row as before (one per account and version, kept after a deletion) — with nothing to tick, and
 * the app goes on. A save that fails says so, with Try again; Sign out is the way out. The
 * Rideboard's own terms are separate (`visitor/rideboard/RideGate.tsx`).
 */
function TermsStep({ version, onDone }: {
  version?: string; confirmed?: string[]; onDone: (next: CompletionState) => void;
}) {
  const tr = useT();
  const { signOut } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const sent = useRef(-1);

  useEffect(() => {
    if (sent.current === attempt) return;
    sent.current = attempt;
    setError(null);
    void api.post<CompletionState>('/me/completion/terms', {
      version: version ?? PLATFORM_TERMS_VERSION,
      confirmations: [...PLATFORM_TERMS_CONFIRMATIONS],
    }).then(onDone, (err: unknown) => {
      setError(err instanceof ApiError ? err.message : tr('That did not save. Try again.'));
    });
  }, [attempt, version, onDone, tr]);

  if (!error) return <LoadingScreen label={tr('One moment…')} />;
  return (
    <Screen title={tr('Terms of Use')} lead={error}>
      <div className="app-grid-14">
        <Button variant="primary" full onClick={() => setAttempt((n) => n + 1)}>{tr('Try again')}</Button>
        <Button variant="secondary" full onClick={() => void signOut()}>{tr('Sign out')}</Button>
      </div>
    </Screen>
  );
}

/** The requirements the profile step answers (`backend/.../completion.ts`). */
export const PROFILE_KEYS = new Set(['name', 'location', 'social']);

interface OwnProfile {
  firstName: string | null; lastName: string | null; country: string | null; city: string | null;
  socials: Record<string, string>;
}

/**
 * First and last name, country and city, and at least one social link (the
 * user, 2026-10-15: "make sure the user profile has all the fields — and they
 * are in the complete-my-profile registration step"; "everything, socials
 * too"). Only what is missing is asked; it saves through `PATCH /me/profile`
 * and asks the server again — the server decides what is complete.
 *
 * On the optional page it is drawn once per part (`missing` one key each, a
 * STABLE array — the profile is re-read whenever it changes), so each part
 * saves on its own.
 */
export function ProfileStep({ missing, onDone, frame = screenFrame, controls }: {
  missing: readonly Pick<Requirement, 'key'>[];
  onDone: (next: CompletionState) => void;
  frame?: StepFrame;
  /** Drawn by the `/complete` wizard: its action bar submits this form, so the form draws no button. */
  controls?: StepControls;
}) {
  const tr = useT();
  const { locale } = useLocale();
  const need = useMemo(() => new Set(missing.map((r) => r.key)), [missing]);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [typed, setTyped] = useState<Record<string, string>>({});
  const [networks, setNetworks] = useState<SocialNetwork[]>(FALLBACK_NETWORKS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void api.get<OwnProfile>('/me/profile').then((p) => {
      if (!live) return;
      setFirst(p.firstName ?? ''); setLast(p.lastName ?? '');
      setCountry(p.country ?? ''); setCity(p.city ?? '');
    }).catch(() => {});
    if (need.has('social')) {
      void api.get<{ networks: SocialNetwork[] }>('/social-networks').then((r) => { if (live && Array.isArray(r?.networks) && r.networks.length) setNetworks(r.networks); }).catch(() => {});
    }
    return () => { live = false; };
  }, [need]);

  const countries = useMemo(() => countryOptions(locale), [locale]);
  const links = Object.fromEntries(networks.flatMap((n) => {
    const link = socialLink(typed[n.slug] ?? '', n.urlPrefix);
    return link ? [[n.slug, link]] : [];
  }));
  const badLink = networks.find((n) => (typed[n.slug] ?? '').trim() && !socialLink(typed[n.slug]!, n.urlPrefix));
  const ready =
    (!need.has('name') || (first.trim() && last.trim())) &&
    (!need.has('location') || (country && city.trim())) &&
    (!need.has('social') || (Object.keys(links).length > 0 && !badLink));
  useReport(controls, 'save', Boolean(ready), busy);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.patch('/me/profile', {
        ...(need.has('name') ? { firstName: first.trim(), lastName: last.trim() } : {}),
        ...(need.has('location') ? { country, city: city.trim() } : {}),
        ...(need.has('social') ? { socials: links } : {}),
      });
      onDone(await api.get<CompletionState>('/me/completion'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tr('That did not save. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  return frame({
    title: tr('Your profile'),
    lead: tr('A few things about you. Other ravers see your first name and the first letter of your last — the rest only if you choose to share it.'),
    children: (
      <form id={controls?.formId} onSubmit={submit} className="app-grid-12">
        {need.has('name') && (
          <>
            <Field label={tr('First name')} type="text" value={first} onChange={setFirst} autoComplete="given-name" maxLength={60} />
            <Field label={tr('Last name')} type="text" value={last} onChange={setLast} autoComplete="family-name" maxLength={60} />
          </>
        )}
        {need.has('location') && (
          <>
            <label className="auth-controls-label">
              <span className="auth-controls-caption">{tr('Country')}</span>
              <select value={country} onChange={(e) => setCountry(e.target.value)} className="auth-controls-input" autoComplete="country">
                <option value="">{tr('Choose a country')}</option>
                {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </label>
            <Field label={tr('City')} type="text" value={city} onChange={setCity} autoComplete="address-level2" maxLength={80} />
          </>
        )}
        {need.has('social') && (
          <fieldset className="complete-account-socials">
            <legend className="auth-controls-caption">{tr('At least one social link')}</legend>
            {networks.map((n) => (
              <Field
                key={n.slug} label={n.label} type="text" value={typed[n.slug] ?? ''}
                onChange={(v) => setTyped((t) => ({ ...t, [n.slug]: v }))}
                placeholder={n.urlPrefix ? '@' : 'https://…'} autoCapitalize="none" autoCorrect="off" spellCheck={false}
              />
            ))}
            <p className="app-p app-faint app-small">{tr('Shared only with the people you choose — never shown on a board.')}</p>
          </fieldset>
        )}
        {badLink && <Alert>{tr('That {network} link is not a web address or a handle.', { network: badLink.label })}</Alert>}
        {error && <Alert>{error}</Alert>}
        {!controls && (
          <Button type="submit" variant="primary" disabled={busy || !ready}>
            {busy ? tr('Saving…') : tr('Save')}
          </Button>
        )}
      </form>
    ),
  });
}

/**
 * What a step is drawn inside. The gate draws each as a whole screen
 * (`Screen`); the optional `/complete` page draws the SAME steps as sections
 * of one page (2026-10-20) — one form, two frames, never two copies.
 *
 * A function called with the parts, never a component: a frame made inline
 * would be a new component type on every render and remount the half-typed
 * form inside it.
 */
export type StepFrame = (parts: {
  title: string; lead: ReactNode; children: ReactNode;
  /**
   * Set when the title and lead belong to the step's CURRENT phase — the code
   * that was sent, the 18+ refusal — so a frame drawing its own title for the
   * step keeps these instead.
   */
  phase?: 'confirm' | 'refused';
}) => ReactNode;

/** Where a step is in its own form — what the wizard's main button says. */
export type StepPhase = 'send' | 'confirm' | 'save' | 'refused';

/**
 * A step drawn by the `/complete` wizard (2026-10-20): the wizard's sticky
 * action bar holds the submit button OUTSIDE the form (`form={formId}`), so the
 * step draws none, and tells the wizard what the button may do.
 */
export interface StepControls {
  formId: string;
  /** A stable callback: told the phase, whether the form can be sent, and whether it is sending. */
  report: (s: { phase: StepPhase; ready: boolean; busy: boolean }) => void;
}

function useReport(controls: StepControls | undefined, phase: StepPhase, ready: boolean, busy: boolean) {
  const report = controls?.report;
  useEffect(() => { report?.({ phase, ready, busy }); }, [report, phase, ready, busy]);
}

const screenFrame: StepFrame = ({ title, lead, children }) => <Screen title={title} lead={lead}>{children}</Screen>;

/** The shell every step of this shares. */
function Screen({ title, lead, children }: { title: string; lead: ReactNode; children: ReactNode }) {
  return (
    <main className="app-outcome is-start">
      <div className="app-step">
        <h1 className="app-title">
          {title}
        </h1>
        <p className="app-lede">{lead}</p>
        {children}
      </div>
    </main>
  );
}

/**
 * Date of birth, and the 18+ gate (`plan.md` §6.13).
 *
 * **The only requirement on this screen that somebody can fail permanently.**
 * Every other one is a thing they do — prove an address, prove a number. A
 * seventeen-year-old cannot do anything, so a refusal here must not read as
 * "try again": it names the date they become eligible and offers nothing else.
 *
 * **Written once.** An editable date makes the whole check a formality, because
 * anybody refused would simply retype it. The API enforces that; this says so
 * before they submit, which is the difference between a rule and a trap.
 *
 * The age rule itself is `@myrave/shared/age` — the same function the API uses,
 * so the warning here and the refusal there can never disagree about a
 * birthday.
 *
 * Optional since 2026-10-20 — drawn on the `/complete` page, not as a gate —
 * and the refusal says the same words there.
 */
export function BirthDateStep({
  requirement, onDone, frame = screenFrame, controls,
}: {
  requirement: Pick<Requirement, 'label' | 'why'>;
  onDone: (next: CompletionState) => void;
  frame?: StepFrame;
  /** Drawn by the `/complete` wizard: its action bar submits this form. */
  controls?: StepControls;
}) {
  const tr = useT();
  const { locale } = useLocale();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState<string | null>(null);

  /*
   * Checked here too, and not as validation theatre: it lets the button say
   * what will happen BEFORE somebody commits a date they cannot change. The
   * API decides; this only warns.
   */
  const parsed = value ? parseCalendarDate(value) : null;
  const today = useMemo<CalendarDate>(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
  }, []);
  const tooYoung = parsed ? !isOldEnough(parsed, today) : false;
  useReport(controls, refused ? 'refused' : 'save', Boolean(parsed) && !refused, busy);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.put<
        { ok: true; completion: CompletionState } | { ok: false; eligibleOn: string }
      >('/me/completion/birth-date', { birthDate: value });

      if (res.ok) onDone(res.completion);
      else setRefused(res.eligibleOn);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tr('That did not save. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  if (refused) {
    return frame({
      phase: 'refused',
      title: tr('MyRave is 18+'),
      lead: rich(
        tr('You can join on {date}. Nothing has been saved, and there is nothing to do until then.'),
        { date: <strong>{formatEligible(refused, locale)}</strong> },
      ),
      /*
       * No form and no retry button, deliberately. There is nothing this
       * person can do, and a button that re-submits would produce the same
       * answer and read as a failure they are supposed to fix.
       */
      children: (
        <p className="app-p app-faint">
          {tr('If you typed the wrong date, an admin can clear it for you.')}
        </p>
      ),
    });
  }

  return frame({
    title: requirement.label,
    lead: requirement.why,
    children: (
      <form id={controls?.formId} onSubmit={submit} className="app-grid-12">
        <BirthDatePicker value={value} onChange={setValue} today={today} />

        {/* Said before they commit, because they cannot change it afterwards. */}
        <p className="app-p app-faint app-small">
          {tr('This is saved once and cannot be edited afterwards.')}
        </p>

        {tooYoung && (
          <Alert>
            {tr('You need to be 18 to use MyRave. Submitting this date will not let you in.')}
          </Alert>
        )}
        {error && <Alert>{error}</Alert>}

        {!controls && (
          <Button type="submit" variant="primary" disabled={busy || !parsed}>
            {busy ? tr('Saving…') : tr('Save')}
          </Button>
        )}
      </form>
    ),
  });
}

/**
 * Day, month and year as three native selects.
 *
 * Not `<input type="date">`: that opens on TODAY, offers every future date,
 * and makes somebody born in 1990 page back through thirty-six years of
 * months. A select is the phone's own scroll wheel, and on a keyboard typing
 * digits jumps to the option — "1990", or "12" for December, because every
 * month is written with its number first.
 *
 * Nothing later than the 18th birthday can be picked: years stop at
 * `today - 18`, and in that last year the months and days stop where
 * `isOldEnough` does. The API still decides; this only keeps a date nobody
 * could be let in with off the screen.
 */
function BirthDatePicker({
  value, onChange, today,
}: {
  value: string;
  onChange: (iso: string) => void;
  today: CalendarDate;
}) {
  const tr = useT();
  const { locale } = useLocale();
  const initial = parseCalendarDate(value);
  const [year, setYear] = useState(initial ? String(initial.year) : '');
  const [month, setMonth] = useState(initial ? String(initial.month) : '');
  const [day, setDay] = useState(initial ? String(initial.day) : '');

  const latestYear = today.year - MINIMUM_AGE;
  const years = useMemo(
    () => Array.from({ length: 101 }, (_, i) => latestYear - i),
    [latestYear],
  );
  const monthName = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }),
    [locale],
  );

  const y = Number(year) || null;
  const m = Number(month) || null;
  /* Without a year, every month and every day of a leap year is a candidate. */
  const allowed = (mm: number, dd: number) =>
    y === null || isOldEnough({ year: y, month: mm, day: dd }, today);
  const months = Array.from({ length: 12 }, (_, i) => i + 1)
    .filter((mm) => allowed(mm, 1));
  const daysIn = m ? new Date(Date.UTC(y ?? 2000, m, 0)).getUTCDate() : 31;
  const days = Array.from({ length: daysIn }, (_, i) => i + 1)
    .filter((dd) => allowed(m ?? 1, dd));

  /*
   * One place turns the three parts into the value, and drops a part that a
   * later choice made impossible (31 after picking February, December after
   * picking the last allowed year) rather than submitting a date nobody chose.
   */
  function update(next: { year?: string; month?: string; day?: string }) {
    const ny = next.year ?? year;
    let nm = next.month ?? month;
    let nd = next.day ?? day;
    const yy = Number(ny) || null;
    if (yy !== null && nm && !isOldEnough({ year: yy, month: Number(nm), day: 1 }, today)) nm = '';
    if (nm && nd) {
      const last = new Date(Date.UTC(yy ?? 2000, Number(nm), 0)).getUTCDate();
      const tooLate = yy !== null && !isOldEnough({ year: yy, month: Number(nm), day: Number(nd) }, today);
      if (Number(nd) > last || tooLate) nd = '';
    }
    setYear(ny); setMonth(nm); setDay(nd);
    onChange(ny && nm && nd ? `${ny}-${nm.padStart(2, '0')}-${nd.padStart(2, '0')}` : '');
  }

  return (
    <fieldset className="complete-account-bday">
      <legend className="auth-controls-caption">{tr('Date of birth')}</legend>
      <div className="complete-account-bday-parts">
        <select
          aria-label={tr('Day')}
          autoComplete="bday-day"
          required
          value={day}
          onChange={(e) => update({ day: e.target.value })}
          className="auth-controls-input"
        >
          <option value="" disabled>{tr('Day')}</option>
          {days.map((dd) => <option key={dd} value={String(dd)}>{dd}</option>)}
        </select>
        <select
          aria-label={tr('Month')}
          autoComplete="bday-month"
          required
          value={month}
          onChange={(e) => update({ month: e.target.value })}
          className="auth-controls-input"
        >
          <option value="" disabled>{tr('Month')}</option>
          {months.map((mm) => (
            <option key={mm} value={String(mm)}>
              {`${String(mm).padStart(2, '0')} · ${monthName.format(new Date(Date.UTC(2000, mm - 1, 1)))}`}
            </option>
          ))}
        </select>
        <select
          aria-label={tr('Year')}
          autoComplete="bday-year"
          required
          value={year}
          onChange={(e) => update({ year: e.target.value })}
          className="auth-controls-input"
        >
          <option value="" disabled>{tr('Year')}</option>
          {years.map((yy) => <option key={yy} value={String(yy)}>{yy}</option>)}
        </select>
      </div>
    </fieldset>
  );
}

/** `2026-03-01` as something a person reads. */
function formatEligible(iso: string, locale: string): string {
  const d = parseCalendarDate(iso);
  if (!d) return iso;
  return new Date(Date.UTC(d.year, d.month - 1, d.day))
    .toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * One blocking requirement, proved.
 *
 * Two steps in one screen — the contact, then the code — because they are one
 * action and a person who has just typed their number should not have to find
 * their way back to a second page.
 *
 * The `/complete` page proves a phone or an email with this same form
 * (2026-10-20), in its own frame and without taking the focus on load.
 */
export function Gate({
  requirement, remaining, prefill, onDone, frame = screenFrame, autoFocus = true, controls,
}: {
  requirement: Pick<Requirement, 'key' | 'label' | 'why'>;
  remaining: number;
  /** An address already on the account, unproven. Empty when there is none. */
  prefill: string;
  onDone: (next: CompletionState) => void;
  frame?: StepFrame;
  /** The address box takes the focus — right for a whole screen, not for one section of a page. */
  autoFocus?: boolean;
  /** Drawn by the `/complete` wizard: its action bar sends (then confirms) this form. */
  controls?: StepControls;
}) {
  const tr = useT();
  const channel = requirement.key === 'phone' ? 'PHONE' : 'EMAIL';
  const [destination, setDestination] = useState(prefill);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useReport(
    controls,
    verificationId ? 'confirm' : 'send',
    verificationId ? code.length === 6 : destination.trim().length >= 3,
    busy,
  );

  // Moving to the next requirement must not carry the previous one's half-typed
  // input with it. Without this the same component is reused and the email is
  // still sitting in the box when the screen asks for a phone number.
  useEffect(() => {
    setDestination(prefill);
    setVerificationId(null);
    setCode('');
    setError(null);
  }, [requirement.key, prefill]);

  async function start(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { id } = await api.post<{ id: string }>('/me/contact/start', {
        channel, destination,
      });
      setVerificationId(id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tr('That did not send. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // The endpoint answers with the NEW completion state, so the screen moves
      // to the next requirement — or out of the way — without a second request.
      const next = await api.post<CompletionState & { confirmed: string }>(
        '/me/contact/confirm', { id: verificationId, code },
      );
      onDone(next);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : tr('That code is not right.'));
      setBusy(false);
    }
  }


  if (verificationId) {
    return frame({
      phase: 'confirm',
      title: tr('Enter the code'),
      lead: rich(
        tr('We sent six digits to {destination}. It is good for ten minutes.'),
        { destination: <strong className="app-strong" dir="ltr">{destination}</strong> },
      ),
      children: (
        <form id={controls?.formId} onSubmit={confirm} className="app-grid-14">
          <Field
            label={tr('Code')}
            type="text"
            value={code}
            onChange={(v) => setCode(v.replace(/\D/g, ''))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            required
            autoFocus
            dir="ltr"
            className="complete-account-code-input"
          />
          {error && <Alert>{error}</Alert>}
          {!controls && (
            <Button type="submit" disabled={busy || code.length !== 6} variant="primary" full>
              {busy ? tr('One moment…') : tr('Confirm')}
            </Button>
          )}
          <button
            type="button"
            onClick={() => { setVerificationId(null); setCode(''); setError(null); }}
            className="app-link-quiet"
          >
            {channel === 'PHONE' ? tr('Use a different phone number') : tr('Use a different email address')}
          </button>
        </form>
      ),
    });
  }

  return frame({
    title:
      prefill ? tr('Confirm your email address')
      : remaining > 1 ? tr('Finish your account')
      : tr('One more thing'),
    lead:
      prefill
        ? tr('We have never checked this address, so we do not yet know it reaches you. Send yourself a code and we will.')
        : requirement.why,
    children: (<>
      <form id={controls?.formId} onSubmit={start} className="app-grid-14">
        {/*
          * **The SAME phone control as the sign-in screen** — the country
          * picker, not a text box with a placeholder showing the shape.
          *
          * It was a plain `tel` input here until 2026-09-26, asking somebody to
          * type `+972…` themselves while the login page beside it offered a
          * picker that builds E.164 for them. Two ways to enter one number is
          * two sets of mistakes to handle, and the one that asks a person to
          * know their own dialling code is the one that gets it wrong.
          */}
        {channel === 'PHONE' ? (
          <PhoneField
            label={requirement.label}
            hint={tr('We will text you a six-digit code.')}
            value={destination}
            onChange={setDestination}
            disabled={busy}
          />
        ) : (
          <Field
            label={requirement.label}
            type="email"
            value={destination}
            onChange={setDestination}
            inputMode="email"
            autoComplete="email"
            placeholder={tr('you@example.com')}
            hint={tr('We will send you a six-digit code to confirm it.')}
            required
            autoFocus={autoFocus}
          />
        )}
        {error && <Alert>{error}</Alert>}
        {!controls && (
          <Button type="submit" disabled={busy || destination.trim().length < 3} variant="primary" full>
            {busy ? tr('Sending…') : tr('Send me a code')}
          </Button>
        )}
      </form>

      {/*
        No "skip". This requirement is blocking, and an escape here would make
        the API's answer decorative — which is exactly the state this screen was
        written to end.
      */}
      {/*
        * **The sentence has to match the step.** It said "this is how you get
        * back into your account if you lose your phone" on BOTH steps — which
        * is true of an email address and nonsense under a field asking for a
        * phone number, where losing the phone is the thing it cannot help with.
        * Reported 2026-09-27.
        */}
      <p className="app-login-fine app-start">
        {remaining > 1 && <>{tr('{count} things left.', { count: remaining })}{' '}</>}
        {channel === 'PHONE'
          ? tr('A code by text works where there is no data at all — which is most of a festival.')
          : tr('This is how you get back into your account if you lose your phone.')}
      </p>
    </>),
  });
}
